/**
 * Resolve vendor NL set-aside / SBA-certification tokens onto the option ids the
 * vendors search actually indexes (SAM set-aside codes, SBA certification UUIDs).
 *
 * Catalogs mirror USFCR-APP-NodeJS providers/vendors VENDORS_SET_ASIDES and
 * VENDORS_SBA_CERTIFICATIONS so NL lookups like { param: 'sbaCertification', text: 'SDVOSB' }
 * become the same { id, value } shape the filter dropdowns emit.
 */

// Self-certified SAM set-asides → setAsides.code
const VENDOR_SET_ASIDES = [
  { id: '27', value: 'Self Certified Small Disadvantaged Business', aliases: [] },
  { id: '23', value: 'Minority Owned Business', aliases: [] },
  { id: '8W', value: 'Women-Owned Small Business (WOSB) Program Set-Aside', aliases: ['WOSB'] },
  { id: 'A2', value: 'Woman Owned Business', aliases: [] },
  { id: 'A5', value: 'Veteran-Owned Small Business Set-Aside', aliases: ['VOSB'] },
  { id: 'OY', value: 'Black American Owned', aliases: [] },
  {
    id: 'QF',
    value: 'Service-Disabled Veteran-Owned Small Business Set-Aside (SDVOSB)',
    aliases: ['SDVOSB', 'SDVOSBC'],
  },
  { id: 'PI', value: 'Hispanic American Owned', aliases: [] },
  { id: '8E', value: 'Economically Disadvantaged Women Owned Small Business', aliases: ['EDWOSB'] },
  { id: 'NB', value: 'Native American Owned', aliases: [] },
  { id: 'QZ', value: 'Subcontinent Asian (Asian-Indian) American Owned', aliases: [] },
  { id: 'FR', value: 'Asian-Pacific American Owned', aliases: [] },
  { id: 'XY', value: 'Indian Tribe (Federally Recognized)', aliases: [] },
  { id: 'OW', value: 'American Indian Owned', aliases: [] },
  { id: '1B', value: 'Tribally Owned Firm', aliases: [] },
  { id: '05', value: 'Alaskan Native Corporation Owned Firm', aliases: [] },
  { id: '8U', value: 'Native Hawaiian Organization Owned Firm', aliases: [] },
];

// Official SBA certifications → sbaDetails.currentCertifications.code (UUID)
// Prefer non-JV 8(a) when the abbrev is ambiguous (shared 8A / 8AN / A6).
const VENDOR_SBA_CERTIFICATIONS = [
  {
    id: 'a9b1d41e-4d2b-4b8d-bb8c-f2a5c8e69cdd',
    value: '8(a) Certification',
    aliases: ['8A', '8AN', 'A6', '8(A)'],
  },
  {
    id: 'c2a1d2ef-3a1e-4c9d-bd16-5bce7fcbf9d3',
    value: 'HUBZone Certification',
    aliases: ['HZC', 'XX', 'HZS', 'HUBZONE'],
  },
  {
    id: 'd3f8a7b6-1e2c-4d3b-98a3-6f28cbcbf92e',
    value: '8(a) Joint Venture - SBA Certified',
    aliases: ['8A JV', '8AN JV'],
  },
  {
    id: 'e8b2d4ef-7a3c-4d56-bbd9-9c3f24a98e1d',
    value: 'Women Owned - SBA Certified',
    aliases: ['WOSB', '8W', 'WOSBSS'],
  },
  {
    id: 'f1a6e7d8-2b4c-4d9e-9b7a-0a9d3e2f8c54',
    value: 'Economically Disadvantaged Women Owned - SBA Certified',
    aliases: ['EDWOSB'],
  },
  {
    id: 'a4c5b2d7-1d3e-4f8b-a3c6-4e9d7b8f1c2e',
    value: 'Veteran-Owned Small Business - SBA Certified',
    aliases: ['VOSB'],
  },
  {
    id: 'b9d0e8f1-4a6c-3d7e-8a9b-5c0e1f2g3h4i',
    value: 'Service-Disabled Veteran-Owned Small Business - SBA Certified',
    aliases: ['SDVOSB'],
  },
];

const CATALOGS = {
  setAside: VENDOR_SET_ASIDES,
  sbaCertification: VENDOR_SBA_CERTIFICATIONS,
};

function normalizeKey(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replaceAll(/[^a-z0-9()]+/g, '');
}

function extractParentheticalAliases(label) {
  const matches = String(label || '').matchAll(/\(([A-Za-z0-9]+)\)/g);
  return [...matches].map((match) => match[1]);
}

function buildLookup(catalog) {
  const byKey = new Map();

  const add = (rawKey, option) => {
    const key = normalizeKey(rawKey);
    if (!key || byKey.has(key)) return;
    byKey.set(key, option);
  };

  catalog.forEach((entry) => {
    const option = { id: entry.id, value: entry.value };
    add(entry.id, option);
    add(entry.value, option);
    (entry.aliases || []).forEach((alias) => add(alias, option));
    extractParentheticalAliases(entry.value).forEach((alias) => add(alias, option));
  });

  return byKey;
}

const LOOKUPS = {
  setAside: buildLookup(VENDOR_SET_ASIDES),
  sbaCertification: buildLookup(VENDOR_SBA_CERTIFICATIONS),
};

function rawCandidates(value) {
  if (value == null || value === '') return [];
  if (typeof value !== 'object') return [value];

  const candidates = [value.id, value.code, value.value, value.name, value.label];
  if (Array.isArray(value.abv)) candidates.push(...value.abv);
  if (Array.isArray(value.id) && value.id !== value.abv) candidates.push(...value.id);
  return candidates.filter((candidate) => candidate != null && candidate !== '');
}

/**
 * Map a raw NL / lookup token onto a vendors filter option when the field has a catalog.
 * Returns null when the field is not resolved or no catalog entry matches.
 */
export function resolveVendorFilterOption(field, value) {
  const lookup = LOOKUPS[field];
  if (!lookup) return null;

  const candidates = rawCandidates(value);
  for (const candidate of candidates) {
    const matched = lookup.get(normalizeKey(candidate));
    if (matched) return { ...matched };
  }

  return null;
}

export function hasVendorFilterCatalog(field) {
  return Boolean(CATALOGS[field]);
}

export { VENDOR_SET_ASIDES, VENDOR_SBA_CERTIFICATIONS };
